import { MODULE_NAME } from './const';
import { getServiceBySlug, getActiveFetchOfActionsForServiceVersion, useAllFetchedDirectoryAppsIds } from './getters';
import {
	SET_SERVICE_ACTIONS,
	SET_SERVICE_DATA,
	SET_SERVICE_SEARCH_OR_CREATE_ACTIONS,
	SET_ACTIVE_FETCH,
	SET_SERVICE_ACTION_COLLECTION_DATA,
	SET_LOADED_STATE_FOR_SERVICE_ACTION_COLLECTION,
	SET_DIRECTORY_APPS,
	BULK_SET_SERVICES_DATA,
	BULK_SET_SERVICE_ACTION_COLLECTIONS_DATA,
} from './mutations';
import state from './state';
import AppService from '../../../utils/AppService';
import StoreModuleService from '../StoreModuleService';
import { bulkSetActionsData } from '../actions/actions';
import api from '@/api';
import AppActionService from '@/utils/AppActionService';
import { arrayToObject, isNullOrUndefined } from '@/utils/helpers';

const buildAppActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);
const _promises = {};

const populateServiceActionCollection = (sac) => ({ ...sac, isServiceActionCollection: true });

// actions declarations
export const setServiceData = buildAppActionObject('setServiceData');
export const updateServiceData = buildAppActionObject('updateServiceData');
export const getServiceData = buildAppActionObject('getServiceData');
export const getAvailableApps = buildAppActionObject('getAvailableApps');
export const getActionsOfApp = buildAppActionObject('getActionsOfApp');
export const setActionsToService = buildAppActionObject('setActionsToService');
export const fetchService = buildAppActionObject('fetchService');
export const fetchAppActions = buildAppActionObject('fetchAppActions');
export const updateAppActions = buildAppActionObject('updateAppActions');
export const getServiceDataBySlug = buildAppActionObject('getServiceDataBySlug');
const _fetchAppActions = buildAppActionObject('_fetchAppActions');
export const setServiceActionCollectionData = buildAppActionObject('setServiceActionCollectionData');
export const fetchServiceActionCollection = buildAppActionObject('fetchServiceActionCollection');
export const updateServiceActionCollectionData = buildAppActionObject('updateServiceActionCollectionData');
export const getServiceActionCollections = buildAppActionObject('getServiceActionCollections');
export const getActionsOfServiceActionCollection = buildAppActionObject('getActionsOfServiceActionCollection');
export const fetchServiceActionCollectionActions = buildAppActionObject('fetchServiceActionCollectionActions');
const _fetchServiceActionCollectionActions = buildAppActionObject('_fetchServiceActionCollectionActions');
export const updateServiceActionCollectionActions = buildAppActionObject('updateServiceActionCollectionActions');
export const getAllDirectoryApps = buildAppActionObject('getAllDirectoryApps');

const fixIfAppIsWidget = (app) =>
	AppService.isSlugOfWidget(app.service_slug) ? { ...app, type: 'interface', service_name: 'Interface' } : app; // TODO: After backend updates widgets type and name, revert this mock data override

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [getServiceData.action]({ dispatch }, payload) {
		const { id, forceGet } = payload;
		const dataIsMissing = isNullOrUndefined(state.data[id]);
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			await (_promises[id] || (_promises[id] = dispatch(fetchService.action, { id })));
		}
	},
	async [getServiceDataBySlug.action]({ dispatch, getters }, payload) {
		const { slug, forceGet } = payload;
		const data = getters[getServiceBySlug.getter](slug);
		const dataIsMissing = isNullOrUndefined(data);
		const needToGet = dataIsMissing || forceGet;
		if (needToGet) {
			await (_promises[slug] || (_promises[slug] = dispatch(fetchService.action, { id: slug })));
		}
	},
	async [getAllDirectoryApps.action]({ commit, getters }, payload) {
		const { page, searchString, specificIds } = payload;
		const requestedIds = specificIds || [];
		const allFetchedIds = getters[useAllFetchedDirectoryAppsIds.getter];
		const idsToFetch = allFetchedIds.filter((fetchedId) => requestedIds.includes(fetchedId));
		const shouldFetch = requestedIds.length === 0 || idsToFetch.length > 0; // if requested specific - fetch only if it doesn't exist, if didn't request specific, always fetch
		if (shouldFetch) {
			const data = await api.getDirectoryApps(page, searchString, specificIds);
			const appsArr = data.results;
			const appsHashmap = appsArr.reduce((a, v) => ({ ...a, [v.id]: v }), {});
			const newDirectoryApps = { ...state.directoryApps, ...appsHashmap };
			commit(SET_DIRECTORY_APPS, newDirectoryApps);
			return { ids: appsArr.map(({ id }) => id), next: data.next, count: data.count };
		}
	},
	[setServiceData.action]({ commit }, payload) {
		const { data: serviceData } = payload;
		commit(SET_SERVICE_DATA, { key: serviceData.id, value: serviceData });
	},
	async [fetchService.action]({ dispatch }, payload) {
		const { id } = payload;
		const ret = await api.getAppData(id);
		await dispatch(updateServiceData.action, { data: fixIfAppIsWidget(ret), forceGet: true });
		return ret;
	},
	async [updateServiceData.action]({ dispatch }, payload) {
		const { data } = payload;
		await dispatch(setServiceData.action, { data });
	},
	[setServiceActionCollectionData.action]({ commit }, payload) {
		const { data: serviceActionCollectionData } = payload;
		commit(SET_SERVICE_ACTION_COLLECTION_DATA, {
			key: serviceActionCollectionData.id,
			value: populateServiceActionCollection(serviceActionCollectionData),
		});
	},
	async [fetchServiceActionCollection.action]({ commit }, payload) {
		const { id } = payload;
		const serviceActionCollection = await api.getServiceActionCollection(id);
		commit(BULK_SET_SERVICE_ACTION_COLLECTIONS_DATA, {
			data: arrayToObject(serviceActionCollection.map(populateServiceActionCollection)),
		});
	},
	async [updateServiceActionCollectionData.action]({ dispatch }, payload) {
		const { data } = payload;
		await dispatch(setServiceActionCollectionData.action, { data });
	},
	async [getAvailableApps.action]({ commit }) {
		const ret = await api.getAvailableApps();
		const mockRes = ret.map(fixIfAppIsWidget);
		commit(BULK_SET_SERVICES_DATA, { data: arrayToObject(mockRes) });
	},
	async [getServiceActionCollections.action]({ dispatch }) {
		const serviceActionCollections = await api.getServiceActionCollections();
		await Promise.all(
			serviceActionCollections.map((serviceActionCollection) =>
				dispatch(updateServiceActionCollectionData.action, { data: serviceActionCollection })
			)
		);
	},
	[getActionsOfServiceActionCollection.action]({ dispatch }, payload) {
		const { id, forceGet } = payload;
		const dataIsMissing = !state.actionsLoadedForCollectionsMap[id];
		const needToGet = dataIsMissing || forceGet;
		const fetchKey = AppActionService.buildServiceActionCollectionActionsFetchKey(id);
		const currentFetchPromise = state.activeAppActionsFetches[fetchKey];
		if (currentFetchPromise) {
			return currentFetchPromise;
		} else if (needToGet) {
			return dispatch(fetchServiceActionCollectionActions.action, { id });
		}
	},
	async [fetchServiceActionCollectionActions.action]({ commit, dispatch }, payload) {
		const { id } = payload;
		const fetchKey = AppActionService.buildServiceActionCollectionActionsFetchKey(id);
		let fetchPromise;
		try {
			fetchPromise = dispatch(_fetchServiceActionCollectionActions.action, payload);
			commit(SET_ACTIVE_FETCH, { key: fetchKey, value: fetchPromise });
			await fetchPromise;
		} finally {
			commit(SET_ACTIVE_FETCH, { key: fetchKey, value: null });
		}
		return fetchPromise;
	},
	async [_fetchServiceActionCollectionActions.action]({ dispatch }, payload) {
		const { id } = payload;
		const allActions = await api.getServiceActionCollectionActions(id);
		return dispatch(updateServiceActionCollectionActions.action, { id, allActions });
	},
	async [updateServiceActionCollectionActions.action]({ commit, dispatch }, payload) {
		const { id, allActions } = payload;
		await dispatch(bulkSetActionsData.fullPath, { data: arrayToObject(allActions) }, { root: true });
		commit(SET_LOADED_STATE_FOR_SERVICE_ACTION_COLLECTION, { key: id, value: true });
	},
	[getActionsOfApp.action]({ dispatch, getters }, payload) {
		const { id, version, forceGet } = payload;
		const data = state.actionIdsByVersion[id]?.[version];
		const dataIsMissing = isNullOrUndefined(data);
		const needToGet = dataIsMissing || forceGet;
		const currentFetchPromise = getters[getActiveFetchOfActionsForServiceVersion.getter](
			AppActionService.buildActionsFetchKey(id, version)
		);
		if (currentFetchPromise) {
			return currentFetchPromise;
		} else if (needToGet) {
			return dispatch(fetchAppActions.action, { id, version });
		}
		return data;
	},
	[setActionsToService.action]({ commit }, payload) {
		const { id, version, actionsIds, searchOrCreatesActionsIds } = payload;
		commit(SET_SERVICE_ACTIONS, { id, version, data: actionsIds });
		commit(SET_SERVICE_SEARCH_OR_CREATE_ACTIONS, { id, version, data: searchOrCreatesActionsIds });
	},
	async [fetchAppActions.action]({ commit, dispatch }, payload) {
		const { id, version } = payload;
		const key = AppActionService.buildActionsFetchKey(id, version);
		let fetchPromise;
		try {
			fetchPromise = dispatch(_fetchAppActions.action, payload);
			commit(SET_ACTIVE_FETCH, { key, value: fetchPromise });
			await fetchPromise;
		} finally {
			commit(SET_ACTIVE_FETCH, { key, value: null });
		}
		return fetchPromise;
	},
	async [_fetchAppActions.action]({ dispatch }, payload) {
		const { id, version } = payload;
		const allActions = await api.getAppActions(id, version);
		return dispatch(updateAppActions.action, { id, version, allActions });
	},
	async [updateAppActions.action]({ dispatch }, payload) {
		const { id, version, allActions } = payload;
		const { actions, searchOrCreates } = allActions;
		const actionsIds = Object.keys(actions);
		await dispatch(bulkSetActionsData.fullPath, { data: actions }, { root: true });
		// map searchOrCreates keys to actions ids
		const searchOrCreatesActionsIds = Object.values(searchOrCreates).map(({ searchAction: searchActionKey }) =>
			Object.values(actions).find((action) => action.key === searchActionKey)
		);

		await dispatch(setActionsToService.action, { id, version, actionsIds, searchOrCreatesActionsIds });
	},
};

export default actions;
